import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Pagination,
  Typography,
} from "@mui/material";
import {
  Phone,
  Email,
  Event,
  Note,
  Notifications,
  SwapHoriz,
  AddCircleOutline,
  Edit,
  Person,
  ArrowForward,
  AccessTime,
} from "@mui/icons-material";

// Icon map for activity types
const activityIcons = {
  Call: Phone,
  Email: Email,
  Meeting: Event,
  Note: Note,
  Reminder: Notifications,
  status_change: SwapHoriz,
  created: AddCircleOutline,
  updated: Edit,
};

const activityIconColors = {
  Call: "#E0F2F1", // teal
  Email: "#E8F0FE", // blue
  Meeting: "#FFF3E0", // orange
  Note: "#F3E5F5", // purple
  Reminder: "#FFF9C4", // yellow
  Status_change: "#FCE4EC", // pink
  Created: "#E8F5E9", // green
  Updated: "#ECEFF1", // grey-blue
};

// Tab panel helper
function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// Clean and crisp card
const ListItem = ({
  icon: Icon,
  activity,
  status,
  iconBgColor = "#E5E7EB",
  title,
  time,
  contactName,
  contactId,
  children,
  contactsMap,
  onContactSelect,
  selected,
  onClick,
}) => (
  <Card
    sx={{
      borderRadius: 2,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      border: selected ? "2px solid #2563EB" : "1px solid transparent",
      backgroundColor: selected ? "rgba(37,99,235,0.05)" : "white",
      "&:hover": {
        boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
      },
      cursor: "pointer",
    }}
    onClick={onClick}
  >
    <CardContent sx={{ px: 2.5, py: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="start">
        {/* LEFT */}
        <Box display="flex" gap={2}>
          <Box
            sx={{
              backgroundColor: iconBgColor,
              p: 1.2,
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "#374151", fontSize: 20 }} />
          </Box>

          <Box>
            <Box>
              <Box fontWeight={600} fontSize="0.95rem" color="text.primary">
                {title}
              </Box>
              <Box fontSize="0.8rem" color="text.secondary" mt={0.3}>
                {children}
              </Box>
            </Box>

            <Box mt={1} display="flex" gap={1.2}>
              <Chip
                label={activity}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.7rem",
                  height: 22,
                  borderRadius: 1.5,
                  px: 0.8,
                }}
              />
              <Chip
                label={status}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  height: 22,
                  backgroundColor: "#E0ECFF",
                  color: "#1E40AF",
                  borderRadius: 1.5,
                  px: 0.8,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* RIGHT */}
        <Box textAlign="right" mt={0.5}>
          <Box
            fontSize="0.75rem"
            color="text.secondary"
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            <AccessTime sx={{ fontSize: 16 }} />
            {time}
          </Box>
          <Box
            onClick={() =>
              onContactSelect?.({ id: contactId, ...contactsMap[contactId] })
            }
            sx={{
              display: "inline-flex",
              alignItems: "center",
              color: "#2563EB",
              fontSize: "0.75rem",
              marginTop: "0.2rem",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <Person sx={{ fontSize: 16, mr: 0.3 }} />
            {contactName}
            <ArrowForward sx={{ fontSize: 14, ml: 0.2 }} />
          </Box>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Loading state
const LoadingSkeleton = () => (
  <div className="space-y-3">
    {[...Array(3)].map((_, i) => (
      <Card key={i}>
        <CardContent className="p-3 flex items-center gap-3">
          <Skeleton variant="circular" width={36} height={36} />
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="60%" height={12} />
            <Skeleton variant="text" width="40%" height={10} />
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

// Main component
export default function DetailedMetricsList({
  activities,
  calls,
  reminders,
  isLoading,
  contactsMap,
  onContactSelect,
  selectedActivityId,
  onActivitySelect,
}) {
  const [tabValue, setTabValue] = useState(0);
  const [pageStates, setPageStates] = useState({
    activities: 1,
    calls: 1,
    reminders: 1,
  });
  const itemsPerPage = 10;
  const handleChange = (_, newValue) => setTabValue(newValue);
  const handlePageChange = (type) => (_, newPage) => {
    setPageStates((prev) => ({ ...prev, [type]: newPage }));
  };
  const paginate = (items, page) => {
    const start = (page - 1) * itemsPerPage;
    return items.slice(start, start + itemsPerPage);
  };

  useEffect(() => {
    setPageStates({
      activities: 1,
      calls: 1,
      reminders: 1,
    });
  }, [activities, calls, reminders]);

  const renderList = (items, type) => {
    if (isLoading) return <LoadingSkeleton />;
    if (!items || items.length === 0) {
      return (
        <>
          <Box my={11.5} />
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "textSecondary" }}
            textAlign="center"
          >
            No {type} for this day.
          </Typography>
        </>
      );
    }
    const currentPage = pageStates[type];
    const paginatedItems = paginate(items, currentPage);
    const totalPages = Math.ceil(items.length / itemsPerPage);

    return (
      <Box>
        <div className="space-y-3">
          {paginatedItems.map((item) => {
            const contact = contactsMap[item.contact] || {};
            const contactName =
              contact.company || contact.first_name || "Unknown";
            const Icon = activityIcons[item.activity_name] || Notifications;
            const status = contact.status_name || "medium";
            const contactId =
              typeof item.contact === "object"
                ? item.contact?.id
                : item.contact;

            return (
              <ListItem
                key={item.id}
                icon={Icon}
                activity={item.activity_name || item.title}
                status={status}
                iconBgColor={
                  activityIconColors[item.activity_name || item.title] ||
                  "#E5E7EB"
                }
                title={item.title}
                time={format(new Date(item.date), "h:mm a")}
                contactName={contactName}
                contactId={contactId}
                contactsMap={contactsMap}
                onContactSelect={onContactSelect}
                selected={selectedActivityId === item.id}
                onClick={() => {
                  onActivitySelect?.(item.id);
                  onContactSelect?.({
                    id: contactId,
                    ...contactsMap[contactId],
                  });
                }}
              >
                {item.description || item.notes || ""}
              </ListItem>
            );
          })}
        </div>
        {totalPages > 1 && (
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange(type)}
              size="small"
              shape="rounded"
              color="primary"
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card
      sx={{
        borderTop: "4px solid #84DEB8",
        borderRadius: 2,
        boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <CardContent className="p-4">
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="All Activities" />
            <Tab label="Calls" />
            <Tab label="Reminders" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderList(activities, "activities")}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {renderList(calls, "calls")}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {renderList(reminders, "reminders")}
          </TabPanel>
        </Box>
      </CardContent>
    </Card>
  );
}
